import { FC, CSSProperties } from 'react';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  Status as PolicyStatus,
  Severity,
} from '@agilelab/plugin-wb-governance-common';
import { makeStyles } from '@material-ui/core';
import {
  ErrorIcon,
  FailedIcon,
  InformationIcon,
  NotBlockingIcon,
  NotStartedIcon,
  OkIcon,
  PassedIcon,
  RunningIcon,
  TerminatedIcon,
  TerminatingIcon,
  WarningIcon,
} from './Icons';

const useStyles = makeStyles({
  testStatusIcon: {
    display: 'inline',
  },
});

const iconByDagStatus: Record<
  DagStatus,
  ((props: Props) => JSX.Element) | undefined
> = {
  [DagStatus.FAILED]: props => <FailedIcon {...props} />,
  [DagStatus.ERROR]: props => <ErrorIcon {...props} />,
  [DagStatus.PASSED]: props => <PassedIcon {...props} />,
  [DagStatus.NOT_EXECUTED]: props => <NotStartedIcon {...props} />,
  [DagStatus.NOT_STARTED]: props => <NotStartedIcon {...props} />,
  [DagStatus.TERMINATED]: props => <TerminatedIcon {...props} />,
  [DagStatus.TERMINATING]: props => <TerminatingIcon {...props} />,
  [DagStatus.RUNNING]: props => <RunningIcon {...props} />,
  [DagStatus.WAITING]: props => <RunningIcon {...props} />,
  [DagStatus.INFO]: props => <InformationIcon {...props} />,
  [DagStatus.NOT_BLOCKING_ERROR]: props => <NotBlockingIcon {...props} />,
  [DagStatus.OK]: props => <OkIcon {...props} />,
  [DagStatus.WARNING]: props => <WarningIcon {...props} />,
};

const statusMessageByDagStatus: Record<DagStatus, string> = {
  [DagStatus.FAILED]: 'Test validation completed with errors.',
  [DagStatus.ERROR]: 'Test validation completed with errors.',
  [DagStatus.PASSED]: 'Test validation completed successfully.',
  [DagStatus.NOT_EXECUTED]: 'Test not executed.',
  [DagStatus.NOT_STARTED]: 'Test not started.',
  [DagStatus.TERMINATED]: 'Test validation terminated.',
  [DagStatus.TERMINATING]: 'Test validation terminating.',
  [DagStatus.RUNNING]: 'Test validation in progress. Please wait.',
  [DagStatus.WAITING]: 'Test validation in progress. Please wait.',
  [DagStatus.INFO]: 'Test information available.',
  [DagStatus.NOT_BLOCKING_ERROR]: 'Test completed with non-blocking errors.',
  [DagStatus.OK]: 'Test validation completed successfully.',
  [DagStatus.WARNING]: 'Test validation completed with warnings.',
};

interface Props {
  status: DagStatus;
  okWithWarnings?: boolean;
  filled?: boolean;
  severity?: Severity;
  policyStatus?: PolicyStatus;
  style?: CSSProperties;
  variant?: 'default' | 'reduced';
}

export const TestStatus: FC<Props> = props => {
  const classes = useStyles();
  const statusIcon = iconByDagStatus[props.status];
  const statusMessage = statusMessageByDagStatus[props.status];

  return (
    <div
      className={classes.testStatusIcon}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {statusMessage && (
        <span
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {statusMessage}
        </span>
      )}
      {statusIcon ? statusIcon(props) : <></>}
    </div>
  );
};
